import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getContentByType, getContentFromCache, getTranslation, ContentDocument } from '../api/contentApi';

const FAQ_CATEGORIES = [
  { key: 'bank-kapitalmarktrecht', de: 'Bank- und Kapitalmarktrecht', fr: 'Droit bancaire et des marchés financiers', en: 'Banking & Capital Markets Law' },
  { key: 'arbeitsrecht', de: 'Arbeitsrecht', fr: 'Droit du travail', en: 'Employment Law' },
  { key: 'mietrecht', de: 'Mietrecht', fr: 'Droit du bail', en: 'Tenancy Law' },
  { key: 'allgemeines-zivilrecht', de: 'Allgemeines Zivilrecht', fr: 'Droit civil général', en: 'General Civil Law' },
  { key: 'zivilprozessrecht', de: 'Zivilprozessrecht', fr: 'Droit de la procédure civile', en: 'Civil Procedure Law' },
];

const FaqPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';

  const [faqs, setFaqs] = useState<ContentDocument[]>(() => getContentFromCache('faq') ?? []);
  const [loading, setLoading] = useState(() => getContentFromCache('faq') === null);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const scrolledToHash = useRef(false);

  useEffect(() => {
    getContentByType('faq')
      .then(setFaqs)
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false));
  }, [t]);

  // When FAQs are loaded and a hash is present, open + scroll to the target entry
  useEffect(() => {
    if (loading || scrolledToHash.current) return;
    const hash = window.location.hash; // e.g. "#faq-abc123"
    if (!hash.startsWith('#faq-')) return;
    const targetId = hash.slice(5); // strip "#faq-"
    setOpenId(targetId);
    scrolledToHash.current = true;
    // Defer scroll until DOM has rendered the open panel
    setTimeout(() => {
      const el = document.getElementById(`faq-${targetId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, [loading, faqs]);

  const filteredFaqs = activeCategory
    ? faqs.filter((f) => f.category === activeCategory)
    : faqs;

  // Only show FAQs that have a published translation in the current language
  const visibleFaqs = filteredFaqs.filter((f) => {
    const tr = getTranslation(f, currentLang);
    return tr !== null;
  });

  return (
    <div>
      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/faq.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('nav.faq')}</h1>
        </div>
      </section>

      <div className="max-w-4xl mx-auto py-12 px-4">
      {loading && <p>{t('common.loading')}</p>}
      {error && <div className="error">{error}</div>}

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => setActiveCategory(null)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${
            activeCategory === null
              ? 'bg-primary-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {t('common.all')}
        </button>
        {FAQ_CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setActiveCategory(cat.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              activeCategory === cat.key
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {cat[currentLang as keyof typeof cat] || cat.de}
          </button>
        ))}
      </div>

      {/* FAQ accordion */}
      {!loading && visibleFaqs.length === 0 ? (
        <p className="text-gray-500">
          {currentLang === 'de' ? 'Keine FAQ-Einträge gefunden.' : currentLang === 'fr' ? 'Aucune entrée FAQ trouvée.' : 'No FAQ entries found.'}
        </p>
      ) : (
        <div className="space-y-3">
          {visibleFaqs.map((faq) => {
            const tr = getTranslation(faq, currentLang)!;
            const isOpen = openId === faq.id;
            const isHighlighted = openId === faq.id && scrolledToHash.current;
            return (
              <div
                key={faq.id}
                id={`faq-${faq.id}`}
                className={`border rounded-lg overflow-hidden scroll-mt-24 transition-colors ${
                  isHighlighted ? 'border-primary-400 ring-2 ring-primary-200' : 'border-gray-200'
                }`}
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : faq.id)}
                  className="w-full flex justify-between items-center px-6 py-4 text-left bg-white hover:bg-gray-50 transition"
                >
                  <span className="font-medium text-primary-800">{tr.question}</span>
                  <span className="text-primary-500 text-xl ml-4">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="prose max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: tr.answer || '' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
};

export default FaqPage;
